import { pickPublicErrorFields } from "@nexra/contracts";
import { isObservationChange, type ObservationChange, type ObservationTopic, type RequestHistorySnapshot } from "@nexra/contracts";
import type { Query, QueryClient } from "@tanstack/react-query";
import { browserApiUrl } from "./api";
import { getLocale } from "./i18n";
import { applyRequestHistorySnapshot, createRequestHistorySnapshotReceiver, mergeRequestHistorySnapshots, type RequestHistoryConnectionStatus, type RequestHistoryView } from "./request-history-stream";
import { privateQueryKey } from "./query-policy";

type Binding = { topic: ObservationTopic; intervalMs: number; kind?: "task" | "request" | "run"; id?: string; scope: "user" | "admin"; snapshot?: boolean };
declare module "@tanstack/react-query" {
  interface Register { queryMeta: { realtime?: Binding; [key: string]: unknown } }
}

export function observationPolicy(topic: ObservationTopic, intervalMs: number, kind?: Binding["kind"], id?: string, scope: Binding["scope"] = "user", snapshot = false) {
  return { refetchOnReconnect: false, refetchOnWindowFocus: false, meta: { realtime: { topic, intervalMs, kind, id, scope, snapshot } } } as const;
}

function matches(query: Query, change: ObservationChange): boolean {
  const binding = query.meta?.realtime;
  if (!binding || binding.topic !== change.topic) return false;
  if (change.resync) return true;
  if (!binding.kind) return !binding.id || binding.id === change.id;
  const ids = binding.kind === "task" ? change.taskIds : binding.kind === "request" ? change.requestIds : undefined;
  if (binding.id && (ids?.includes(binding.id) || change.id === binding.id)) return true;
  const row = query.state.data as { id?: string; requestId?: string; taskId?: string; tasks?: readonly { id: string; requestId?: string }[]; models?: readonly string[]; repetitions?: number } | undefined;
  if (!row) return false;
  if (binding.kind === "task") return row?.id === binding.id && Boolean(pickPublicErrorFields(row).nexraRequestId && change.requestIds?.includes(pickPublicErrorFields(row).nexraRequestId!));
  if (binding.kind === "request") return pickPublicErrorFields(row).nexraRequestId === binding.id && Boolean(row.taskId && change.taskIds?.includes(row.taskId));
  if (row.id !== binding.id) return false;
  if (row.tasks?.some(task => change.taskIds?.includes(task.id) || (pickPublicErrorFields(task).nexraRequestId && change.requestIds?.includes(pickPublicErrorFields(task).nexraRequestId!)))) return true;
  // During run creation, a committed task may not yet be in the first run response.
  return Boolean(change.taskIds?.length && row.tasks && row.models && row.repetitions && row.tasks.length < row.models.length * row.repetitions);
}

export function connectObservation(client: QueryClient, userId: string, status: (value: RequestHistoryConnectionStatus) => void, onNewRecords?: () => void) {
  let source: EventSource | undefined;
  let sourceUrl: string | undefined;
  let stopped: false | "closed" | "unavailable" | "signed-out" = false;
  let ready = false;
  let degraded = false;
  let signature = "";
  let retry: ReturnType<typeof setTimeout> | undefined;
  let watchdog: ReturnType<typeof setInterval> | undefined;
  let lastHeartbeat = Date.now();
  let retryMs = 1000;
  let view: RequestHistoryView = { frontPage: true, taskIds: [], requestIds: [] };
  let viewKey = "";
  let viewTargetsKey = "";
  let newRecordsAvailable = false;
  let snapshot: RequestHistorySnapshot | undefined;
  let applying = false;
  let receiver: ReturnType<typeof createRequestHistorySnapshotReceiver> | undefined;
  const pending = new Map<Query, { dirty: boolean; startedAt: number; timer?: ReturnType<typeof setTimeout> }>();
  const active = () => client.getQueryCache().getAll().filter(query => query.meta?.realtime && query.isActive() && query.queryKey[1] === "account" && query.queryKey[2] === userId);
  const notifyNewRecords = () => { if (!newRecordsAvailable) { newRecordsAvailable = true; onNewRecords?.(); } };
  const disconnect = () => {
    source?.close(); source = undefined; sourceUrl = undefined; ready = false; snapshot = undefined; receiver?.reset();
    if (watchdog) clearInterval(watchdog); watchdog = undefined;
    for (const state of pending.values()) { if (state.timer) clearTimeout(state.timer); state.timer = undefined; }
  };
  const flush = () => {
    if (stopped || applying || !source || !snapshot) return;
    const keys = ["requests", "tasks"].map(family => privateQueryKey(family, userId, { view: "history", limit: 20 }));
    keys.push(...snapshot.requests.map(row => privateQueryKey("request", userId, { id: row.nexraRequestId })), ...snapshot.removedRequestIds.map(id => privateQueryKey("request", userId, { id })));
    if (keys.some(key => { const state = client.getQueryState(key); return state && state.fetchStatus !== "idle"; })) return;
    applying = true;
    const frame = snapshot; snapshot = undefined;
    try {
      if (frame.requestDetailsComplete !== false) for (const row of frame.requests) {
        const query = client.getQueryCache().find({ queryKey: privateQueryKey("request", userId, { id: row.nexraRequestId }), exact: true });
        const state = query && pending.get(query);
        if (state) { state.dirty = false; if (state.timer) clearTimeout(state.timer); state.timer = undefined; }
      }
      if (!view.frontPage) {
        const requests = client.getQueryData<{ pages: { data: { nexraRequestId: string }[] }[] }>(keys[0]!);
        const tasks = client.getQueryData<{ pages: { data: { id: string }[] }[] }>(keys[1]!);
        const knownRequests = new Set(requests?.pages.flatMap(page => page.data.map(row => row.nexraRequestId)));
        const knownTasks = new Set(tasks?.pages.flatMap(page => page.data.map(row => row.id)));
        if (frame.newRequestIds.some(id => !knownRequests.has(id)) || frame.newTaskIds.some(id => !knownTasks.has(id))) notifyNewRecords();
      }
      if (applyRequestHistorySnapshot(client, userId, frame, view.frontPage, false)) open(true);
    } finally { applying = false; }
  };
  const schedule = (query: Query) => {
    const binding = query.meta?.realtime;
    const state = pending.get(query);
    if (!state?.dirty || state.timer || !binding || !query.isActive() || query.state.fetchStatus !== "idle" || document.hidden || stopped || !source || !ready) return;
    // Preserve an already fetched cursor window; discovery never shifts old pages.
    const data = query.state.data as { pages?: unknown[] } | undefined;
    if (data?.pages && data.pages.length > 1) { state.dirty = false; query.invalidate(); notifyNewRecords(); return; }
    state.timer = setTimeout(() => {
      state.timer = undefined;
      if (stopped || !source || !ready || document.hidden || !query.isActive() || query.state.fetchStatus !== "idle") return;
      state.dirty = false; state.startedAt = Date.now();
      void client.invalidateQueries({ queryKey: query.queryKey, exact: true, refetchType: "active" }, { cancelRefetch: false });
    }, Math.max(0, state.startedAt + binding.intervalMs - Date.now()));
  };
  const dirty = (query: Query) => {
    const state = pending.get(query) ?? { dirty: false, startedAt: query.state.dataUpdatedAt || Date.now() };
    state.dirty = true; pending.set(query, state); schedule(query);
  };
  const failed = () => {
    disconnect(); degraded = true; status("degraded");
    if (stopped || retry || document.hidden || !active().length) return;
    retry = setTimeout(() => { retry = undefined; open(); }, retryMs);
    retryMs = Math.min(30_000, retryMs * 2);
  };
  const open = (force = false) => {
    if (stopped || document.hidden) { disconnect(); return; }
    const queries = active(); if (!queries.length) { disconnect(); return; }
    const admin = queries.some(query => query.meta!.realtime!.scope === "admin");
    const topics = [...new Set(queries.map(query => query.meta!.realtime!.topic))].sort();
    const snapshots = queries.some(query => query.meta!.realtime!.snapshot);
    const params = new URLSearchParams({ observe: admin ? "admin" : "user", topics: topics.join(","), locale: getLocale() });
    if (snapshots) {
      params.set("history", "1");
      const requestIds = [...new Set(view.requestIds)].slice(0, 100);
      const taskIds = [...new Set(view.taskIds)].slice(0, 100 - requestIds.length);
      if (requestIds.length) params.set("request_ids", requestIds.join(","));
      if (taskIds.length) params.set("task_ids", taskIds.join(","));
    }
    const url = browserApiUrl(`v1/console/request-history/events?${params}`);
    if (!force && source && sourceUrl === url) return;
    if (retry) clearTimeout(retry); retry = undefined;
    disconnect();
    const openedAt = Date.now();
    const next = new EventSource(url, { withCredentials: true });
    source = next; sourceUrl = url; lastHeartbeat = Date.now(); status(degraded ? "degraded" : "connecting");
    const listen = (name: string, fn: (event: MessageEvent<string>) => void) => next.addEventListener(name, event => {
      if (!stopped && source === next) fn(event as MessageEvent<string>);
    });
    listen("history.ready", () => {
      ready = true; degraded = false; lastHeartbeat = Date.now(); retryMs = 1000; status("live");
      for (const query of active()) if (!query.meta!.realtime!.snapshot && (query.state.fetchStatus !== "idle" || query.state.dataUpdatedAt <= openedAt)) dirty(query);
      for (const query of active()) schedule(query);
    });
    listen("history.ping", () => { lastHeartbeat = Date.now(); });
    listen("history.changed", event => {
      try {
        const change: unknown = JSON.parse(event.data);
        if (!isObservationChange(change)) { failed(); return; }
        lastHeartbeat = Date.now();
        for (const query of active()) {
          const binding = query.meta!.realtime!;
          if (!binding.snapshot && (binding.scope === "admin" || change.userId === userId) && matches(query, change)) dirty(query);
        }
      } catch { failed(); }
    });
    const halt = (value: "unavailable" | "signed-out") => { stopped = value; disconnect(); if (retry) clearTimeout(retry); retry = undefined; pending.clear(); status(value); };
    receiver = createRequestHistorySnapshotReceiver(frame => {
      if (frame.accountId !== userId) { halt("signed-out"); void client.invalidateQueries({ queryKey: ["session"] }); return; }
      snapshot = snapshot ? mergeRequestHistorySnapshots(snapshot, frame) : frame;
      degraded = false; lastHeartbeat = Date.now(); status("live"); flush();
    }, failed);
    const snapshotReceiver = receiver;
    listen("history.snapshot", event => snapshotReceiver.snapshot(event.data));
    listen("history.snapshot-chunk", event => { lastHeartbeat = Date.now(); snapshotReceiver.chunk(event.data); });
    listen("history.reconnect", failed);
    listen("history.unavailable", () => halt("unavailable"));
    listen("history.auth-expired", () => { halt("signed-out"); void client.invalidateQueries({ queryKey: ["session"] }); });
    next.onerror = () => { if (source === next && !stopped) failed(); };
    watchdog = setInterval(() => { if (Date.now() - lastHeartbeat > 45_000) failed(); }, 15_000);
  };
  const update = () => {
    if (stopped) return;
    const queries = active();
    const activeQueries = new Set(queries);
    for (const [query, state] of pending) if (!activeQueries.has(query)) { if (state.timer) clearTimeout(state.timer); pending.delete(query); }
    const next = JSON.stringify([queries.some(query => query.meta!.realtime!.scope === "admin"), [...new Set(queries.map(query => query.meta!.realtime!.topic))].sort(), queries.some(query => query.meta!.realtime!.snapshot)]);
    if (next !== signature) { signature = next; if (retry) clearTimeout(retry); retry = undefined; open(); }
    flush(); for (const query of queries) schedule(query);
  };
  const unsubscribe = client.getQueryCache().subscribe(event => {
    if (event.type === "removed") { const state = pending.get(event.query); if (state?.timer) clearTimeout(state.timer); pending.delete(event.query); }
    if (event.type === "updated" && event.action.type === "fetch") {
      const state = pending.get(event.query) ?? { dirty: false, startedAt: Date.now() };
      state.startedAt = Date.now(); pending.set(event.query, state);
    }
    update();
  });
  const visibility = () => { if (stopped) return; if (retry) clearTimeout(retry); retry = undefined; if (document.hidden) { disconnect(); status(degraded ? "degraded" : "connecting"); } else open(); };
  const reconnect = () => { if (stopped === "unavailable") stopped = false; open(true); };
  document.addEventListener("visibilitychange", visibility); update();
  return {
    watchHistory(targets: Omit<RequestHistoryView, "frontPage">, key: string, frontPage: boolean) {
      view = { ...targets, frontPage };
      const targetKey = JSON.stringify([[...new Set(targets.requestIds)].sort(), [...new Set(targets.taskIds)].sort()]);
      const changed = key !== viewKey || (!frontPage && targetKey !== viewTargetsKey);
      viewKey = key; viewTargetsKey = targetKey;
      if (changed && (targets.requestIds.length || targets.taskIds.length || !frontPage)) open();
    },
    reconnect,
    refreshHistory() { newRecordsAvailable = false; view = { ...view, frontPage: true }; if (stopped === "unavailable") stopped = false; open(!ready); },
    close() { stopped = "closed"; disconnect(); if (retry) clearTimeout(retry); for (const state of pending.values()) if (state.timer) clearTimeout(state.timer); pending.clear(); unsubscribe(); document.removeEventListener("visibilitychange", visibility); },
  };
}
